import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { STALE_TIME } from '@/api/staleTimes';
import {
  pcmpEditGrantSchema,
  pcmpEditPermissionItemsSchema,
  pcmpGrantableUsersSchema,
  type PcmpEditGrant,
  type PcmpEditPermissionItem,
  type PcmpGrantableUser,
} from '../types/pcmpEditPermissions.types';

/** The API finds nobody for a shorter term, so the picker does not ask. */
export const PCMP_GRANTABLE_MIN_CHARS = 2;

/** The search input caps what is typed. */
export const PCMP_GRANTABLE_MAX_CHARS = 100;

export const pcmpEditPermissionQueryKeys = {
  all: () => ['pcmp-edit-permissions'] as const,
  list: () => [...pcmpEditPermissionQueryKeys.all(), 'list'] as const,
  grant: (userId: number) => [...pcmpEditPermissionQueryKeys.all(), 'grant', userId] as const,
  users: (term: string) => [...pcmpEditPermissionQueryKeys.all(), 'users', term] as const,
};

/** Every grant, live ones first and newest change first (PCMP admins only). */
export const pcmpEditPermissionsQueryOptions = () =>
  queryOptions({
    queryKey: pcmpEditPermissionQueryKeys.list(),
    queryFn: async (): Promise<PcmpEditPermissionItem[]> => {
      const res = await apiClient.get(API.pcmpEditPermission.list);
      return pcmpEditPermissionItemsSchema.parse(res.data);
    },
    staleTime: 0,
  });

/** One user's grants, so the form can pre-tick them. */
export const pcmpEditGrantQueryOptions = (userId: number | null) =>
  queryOptions({
    queryKey: pcmpEditPermissionQueryKeys.grant(userId ?? 0),
    queryFn: async (): Promise<PcmpEditGrant> => {
      const res = await apiClient.get(API.pcmpEditPermission.one(userId ?? 0));
      return pcmpEditGrantSchema.parse(res.data);
    },
    enabled: userId !== null,
    staleTime: 0,
  });

/** At most 20 active General Users by name, username or HR id. */
export const pcmpGrantableUsersQueryOptions = (term: string) => {
  const q = term.trim();
  return queryOptions({
    queryKey: pcmpEditPermissionQueryKeys.users(q),
    queryFn: async (): Promise<PcmpGrantableUser[]> => {
      const res = await apiClient.get(API.pcmpEditPermission.users, { params: { q } });
      return pcmpGrantableUsersSchema.parse(res.data);
    },
    enabled: q.length >= PCMP_GRANTABLE_MIN_CHARS,
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });
};

/** Sets a user's grants; both false withdraws them. */
export async function savePcmpEditPermission(
  userId: number,
  grant: Pick<PcmpEditGrant, 'editAnyStage' | 'proposalCorrection'>,
): Promise<PcmpEditGrant> {
  const res = await apiClient.put(API.pcmpEditPermission.one(userId), grant);
  return pcmpEditGrantSchema.parse(res.data);
}
